// /ltr/$segment/for/$nationality — Triple-key TH page (LTR × nationality)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { LTR_SEGMENTS } from "@/data/ltr-segments";
import { VISA_NATIONALITIES } from "@/data/visa-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { isIndexable } from "@/data/indexable-whitelist";
import { ContactCTA, FaqAccordion, DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, FileText, Wallet, Clock, ShieldCheck, BadgeCheck, Globe2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const GOV_FEE = 50000;

export const Route = createFileRoute("/ltr/$segment/for/$nationality")({
  loader: ({ params }) => {
    const seg = LTR_SEGMENTS.find((x) => x.slug === params.segment);
    const nat = VISA_NATIONALITIES.find((n) => n.slug === params.nationality);
    if (!seg || !nat) throw notFound();
    return { seg, nat };
  },
  head: ({ params }) => {
    const seg = LTR_SEGMENTS.find((x) => x.slug === params.segment);
    const nat = VISA_NATIONALITIES.find((n) => n.slug === params.nationality);
    if (!seg || !nat) return { meta: [{ title: ctaTitle("ไม่พบหน้า", "/ltr/$segment/for/$nationality", "th")}] };
    const url = `${SITE}/ltr/${seg.slug}/for/${nat.slug}`;
    const title = `${seg.titleTh} สำหรับชาว${nat.nameTh} — เงื่อนไข ขั้นตอน ค่าธรรมเนียม 2026 | NYC`;
    const desc = `ยื่นขอ ${seg.titleTh} สำหรับพลเมือง${nat.countryEn} — ${seg.qualifyTh}. ค่าธรรมเนียม BOI 50,000 บาท + บริการ NYC ${seg.servicePriceTHB.toLocaleString()} บาท วีซ่า 10 ปี + Digital Work Permit + สิทธิภาษีพิเศษ. ${nat.visaExempt ? `เข้าไทย Visa Exempt ${nat.visaExempt} วัน` : "ต้องยื่นวีซ่าก่อนเข้าไทย"}.`;
    const faqs = [
      { q: `พลเมือง${nat.countryEn}ขอ ${seg.titleTh} ได้ไหม?`, a: `ได้ — LTR Visa เปิดรับทุกสัญชาติรวมถึง${nat.countryEn} ยื่นออนไลน์ที่ ltr.boi.go.th ก่อนแล้วไปปั๊มวีซ่าที่สถานทูตไทยใน${nat.countryEn} หรือ OSS Chamchuri Square (ในกรณีอยู่ในไทยแล้ว)` },
      { q: `ชาว${nat.countryEn} ต้องเข้าไทยก่อนยื่นไหม?`, a: nat.visaExempt > 0 ? `ไม่จำเป็น — สามารถยื่น qualification ออนไลน์จาก${nat.countryEn}ก่อน หรือเข้าไทย Visa Exempt ${nat.visaExempt} วันแล้วยื่นที่ Chamchuri Square ก็ได้` : `แนะนำให้ยื่น qualification ออนไลน์จาก${nat.countryEn}ก่อน แล้วไปปั๊มวีซ่าที่สถานทูตไทยในประเทศ` },
      { q: `รายได้/สินทรัพย์ต้องพิสูจน์อย่างไร?`, a: `${seg.qualifyTh} — เอกสารต้องเป็นภาษาอังกฤษ หรือแปลรับรอง MFA. NYC ตรวจ eligibility ก่อนยื่นและเตรียมเอกสารครบในที่เดียว` },
      { q: `LTR ได้สิทธิภาษีอะไรบ้างสำหรับชาว${nat.countryEn}?`, a: `${seg.perksTh} — รวมถึง Royal Decree 743 ยกเว้นภาษีจาก foreign-sourced income ที่นำเข้าไทย (มีผล 1 ม.ค. 2024) ใช้ได้กับผู้ถือ LTR ทุกสัญชาติรวม${nat.countryEn}` },
      { q: `ราคารวมของ NYC เท่าไหร่?`, a: `บริการเตรียมเอกสาร+แปล+ยื่น BOI+ตามผล ${seg.servicePriceTHB.toLocaleString()} บาท + ค่าธรรมเนียม BOI 50,000 บาท รวม ${(seg.servicePriceTHB + GOV_FEE).toLocaleString()} บาท` },
      { q: `ใช้เวลาดำเนินการกี่วัน?`, a: `${seg.processingDays} หลังยื่นครบเอกสาร — BOI ตรวจ qualification 20 วันทำการ แล้วออก endorsement letter ไปปั๊มวีซ่า` },
    ];
    return {
      meta: [
        ...(isIndexable(url) ? [] : [{ name: "robots", content: "noindex,follow" }]),
        { title: ctaTitle(title, "/ltr/$segment/for/$nationality", "th") }, { name: "description", content: ctaDesc(desc, "/ltr/$segment/for/$nationality", "th")},
        { name: "keywords", content: `LTR ${nat.demonymEn}, ${seg.titleTh} ${nat.nameTh}, Long Term Resident ${nat.countryEn}, ${nat.keywords.join(", ")}` },
        { property: "og:title", content: ctaTitle(title, "/ltr/$segment/for/$nationality", "th")}, { property: "og:description", content: ctaDesc(desc, "/ltr/$segment/for/$nationality", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/ltr/${seg.slug}/for/${nat.slug}`, `/en/ltr/${seg.slug}/for/${nat.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${seg.titleTh} สำหรับชาว${nat.nameTh}`,
              serviceType: `${seg.titleEn} for ${nat.demonymEn} citizens`, description: desc,
              inLanguage: "th",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: nat.countryEn }],
              offers: { "@type": "Offer", price: String(seg.servicePriceTHB), priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          howToSchema({
            name: `วิธีขอ ${seg.titleTh} สำหรับชาว${nat.nameTh}`,
            description: `${seg.taglineTh} — guide สำหรับพลเมือง${nat.countryEn}`,
            totalTime: "P30D",
            estimatedCost: { currency: "THB", value: String(seg.servicePriceTHB + GOV_FEE) },
            inLanguage: "th",
            steps: seg.stepsTh,
          }),
          faqPageSchema(faqs),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "LTR Visa", item: `${SITE}/ltr` },
                { "@type": "ListItem", position: 3, name: seg.titleTh, item: `${SITE}/ltr/${seg.slug}` },
                { "@type": "ListItem", position: 4, name: nat.countryEn, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 2).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: LtrSegmentNationalityPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้า</h1>
      <Link to="/ltr" className="mt-4 inline-block text-amber-300 underline">กลับหน้า LTR</Link>
    </main>
  ),
});

function LtrSegmentNationalityPage() {
  const { seg, nat } = Route.useLoaderData();
  const otherNats = VISA_NATIONALITIES.filter((n) => n.slug !== nat.slug).slice(0, 12);
  const otherSegs = LTR_SEGMENTS.filter((s) => s.slug !== seg.slug);
  const faqs = [
    { q: `พลเมือง${nat.countryEn}ขอ ${seg.titleTh} ได้ไหม?`, a: `ได้ — เปิดรับทุกสัญชาติรวม${nat.countryEn} ยื่นออนไลน์ที่ ltr.boi.go.th แล้วปั๊มวีซ่าที่สถานทูตไทยใน${nat.countryEn} หรือ Chamchuri Square` },
    { q: `ต้องเข้าไทยก่อนยื่นไหม?`, a: nat.visaExempt > 0 ? `ไม่ต้อง — เข้า Visa Exempt ${nat.visaExempt} วันแล้วยื่นในไทยได้` : `แนะนำยื่น qualification ก่อนเดินทาง` },
    { q: `เอกสารต้องแปลภาษาไหน?`, a: `ภาษาอังกฤษ หรือแปลไทยรับรอง MFA — NYC ดำเนินการครบในที่เดียว` },
    { q: `LTR ได้สิทธิภาษีอะไร?`, a: `${seg.perksTh} + Royal Decree 743 ยกเว้นภาษีจากรายได้ต่างประเทศที่นำเข้าไทย` },
    { q: `ราคารวมเท่าไหร่?`, a: `${seg.servicePriceTHB.toLocaleString()} + ${GOV_FEE.toLocaleString()} บาท (BOI) = ${(seg.servicePriceTHB + GOV_FEE).toLocaleString()} บาท` },
    { q: `ใช้เวลากี่วัน?`, a: `${seg.processingDays} — BOI ตรวจ qualification 20 วันทำการ` },
  ];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/ltr" className="hover:text-gold">LTR</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/ltr/$segment" params={{ segment: seg.slug }} className="hover:text-gold">{seg.titleTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{nat.flag} {nat.countryEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Sparkles className="h-3.5 w-3.5" /> {seg.icon} LTR · {nat.demonymEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {seg.titleTh}<br />สำหรับพลเมือง<span className="text-gold">{nat.countryEn}</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {seg.taglineTh} — guide เฉพาะของชาว{nat.nameTh}: {nat.visaExempt ? `เข้า Visa Exempt ${nat.visaExempt} วันก่อนยื่นได้` : "ต้องยื่นก่อนเดินทาง"} ค่าธรรมเนียม BOI 50,000 + บริการ NYC {seg.servicePriceTHB.toLocaleString()} บาท วีซ่า 10 ปี.
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ค่าธรรมเนียมรวม", v: `฿${(seg.servicePriceTHB + GOV_FEE).toLocaleString()}` },
              { k: "ระยะเวลา", v: seg.processingDays },
              { k: "อายุวีซ่า", v: "10 ปี" },
              { k: "Visa Exempt", v: nat.visaExempt ? `${nat.visaExempt} วัน` : "ไม่มี" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> แชต LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC ช่วยพลเมือง{nat.countryEn}ยื่น {seg.titleTh} — ตรวจ eligibility ({seg.qualifyTh}) เตรียมเอกสาร แปลรับรอง MFA และยื่น qualification ผ่าน ltr.boi.go.th — เสร็จใน {seg.processingDays} ราคา {seg.servicePriceTHB.toLocaleString()} บาท + ค่าธรรมเนียม BOI 50,000 บาท เหมาะกับ{seg.whoTh}
        </blockquote>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <DirectAnswer entity={`LTR สำหรับ${nat.countryEn}`}>
            {seg.quickAnswerTh} สำหรับพลเมือง{nat.countryEn} {nat.notes}
          </DirectAnswer>
          <KeyFactsTable rows={[
            { k: "สัญชาติ", v: `${nat.flag} ${nat.countryEn}` },
            { k: "หมวด LTR", v: seg.titleEn },
            { k: "เกณฑ์คุณสมบัติ", v: seg.qualifyTh },
            { k: "ค่าธรรมเนียม BOI", v: `฿${GOV_FEE.toLocaleString()}` },
            { k: "บริการ NYC", v: `฿${seg.servicePriceTHB.toLocaleString()}` },
            { k: "อายุวีซ่า", v: "10 ปี (5+5)" },
          ]} />
        </div>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ทำไมพลเมือง{nat.countryEn}เลือก {seg.titleTh}</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            {nat.notes} ทำให้ {seg.titleTh} เป็นทางเลือกระดับ premium สำหรับ{nat.demonymEn}ที่ต้องการพำนักไทยระยะยาว 10 ปีพร้อมสิทธิภาษีและ Digital Work Permit. {seg.perksTh}
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            จุดต่างของ NYC: ทีม Notarial Services Attorney ขึ้นทะเบียนสภาทนายความ + แปลรับรอง MFA + ประสบการณ์ยื่น LTR กับ BOI ตรงในที่เดียว — พลเมือง{nat.countryEn}ส่งเอกสารผ่าน LINE/email ได้ ไม่ต้องเดินทางมา. ดำเนินการตั้งแต่ qualification review จนรับ Endorsement letter และปั๊มวีซ่า ใน {seg.processingDays}.
          </p>
        </section>

        <section className="mt-12 grid gap-5 sm:grid-cols-2">
          {[
            { icon: FileText, title: "เอกสารหลัก", items: seg.docsTh as string[] },
            { icon: ShieldCheck, title: "เกณฑ์คุณสมบัติ", items: [seg.qualifyTh, seg.whoTh] as string[] },
            { icon: Clock, title: "Timeline", items: seg.stepsTh.map((s: { name: string; text: string }) => `${s.name} — ${s.text.slice(0, 70)}…`) },
            { icon: Wallet, title: "ค่าใช้จ่ายรวม", items: [`บริการ NYC ฿${seg.servicePriceTHB.toLocaleString()}`, `ค่าธรรมเนียม BOI ฿${GOV_FEE.toLocaleString()}`, "Health insurance USD 50,000+", "Notary +฿1,500/เอกสาร"] as string[] },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex items-center gap-2"><b.icon className="h-5 w-5 text-amber-600" /><h3 className="font-display text-lg font-extrabold text-navy-deep">{b.title}</h3></div>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                {b.items.slice(0, 6).map((it: string, i: number) => <li key={i} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{it}</li>)}
              </ul>
            </div>
          ))}
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">แพ็กเกจสำหรับ{nat.countryEn}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">฿{seg.servicePriceTHB.toLocaleString()} — end-to-end</h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ตรวจ eligibility + เตรียมเอกสาร {seg.docsTh.length} รายการ</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> แปลภาษาอังกฤษ + รับรอง MFA</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ยื่น qualification ที่ ltr.boi.go.th + ตามผล</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ประสานสถานทูต/OSS Chamchuri ปั๊มวีซ่า + Digital Work Permit</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={faqs} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">หมวด LTR อื่นๆ สำหรับชาว{nat.nameTh}</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherSegs.map((s) => (
              <Link key={s.slug} to="/ltr/$segment/for/$nationality" params={{ segment: s.slug, nationality: nat.slug }} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <span>{s.icon}</span>
                <span className="flex-1 font-semibold text-slate-800 group-hover:text-amber-700">{s.titleTh}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{seg.titleTh} สำหรับสัญชาติอื่น</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherNats.map((n) => (
              <Link key={n.slug} to="/ltr/$segment/for/$nationality" params={{ segment: seg.slug, nationality: n.slug }} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <Globe2 className="h-4 w-4 text-amber-600" />
                <span className="flex-1 font-semibold text-slate-800 group-hover:text-amber-700">{n.flag} {n.countryEn}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
